import styled from 'styled-components';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { riseIn } from '@shared/theme/animations';
import { IconArrowLeft } from '@shared/ui/icons';
import { ReadOnlyBanner, useAgentPermission } from '@entities/user';

const Wrap = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing(7)};
  animation: ${riseIn} 420ms ${({ theme }) => theme.motion.easeOut};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
  min-height: 32px;
`;

const Back = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  text-decoration: none;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.md};
  transition:
    background ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut},
    color ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.text.primary};
  }

  & > svg {
    transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};
  }

  &:hover > svg {
    transform: translateX(-3px);
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;

  & .accent {
    color: ${({ theme }) => theme.colors.primary};
    font-weight: 800;
  }
`;

const SectionTitle = styled.h1`
  margin: 0;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 15px;
  margin-top: ${({ theme }) => theme.spacing(2)};
  max-width: 760px;
`;

const SECTIONS: Record<string, { title: string; subtitle?: string }> = {
  'volume-checks': {
    title: 'Проверка объёмов работ',
    subtitle:
      'Загрузите рабочую документацию (РД, обычно ZIP с PDF) и контракт (ВОР/РДЦ — Excel, PDF или Word). Агент извлечёт позиции объёмов из обеих сторон для дальнейшей сверки.',
  },
  'write-off-checks': {
    title: 'Списание материалов (М-29)',
    subtitle:
      'Загрузите акты КС-2 (источник объёмов) и накладные списания (ТОРГ-12/УПД/М-11, Excel или PDF). Агент посчитает нормативный расход материалов по ГЭСН/ФЕР, распознает фактическое списание и выдаст баланс М-29: перерасход / экономию со стоимостью.',
  },
  'ks-checks': {
    title: 'Проверка актов КС-2/КС-3',
    subtitle:
      'Загрузите договорную смету (эталон, ЛСР или РДЦ/BOQ) с привязкой к договору и пакет «выполнение» (КС-3 + акты КС-2). Агент сверит цены, объёмы и арифметику позиций с договорной сметой, проверит reconciliation КС-3 (Σ КС-2, НДС, аванс, удержание) и выдаст расхождения с HITL-разметкой и XLSX-отчётом.',
  },
  'ks-packages': {
    title: 'Пакет акта (КС-2 / КС-3)',
    subtitle:
      'Сборка комплекта закрытия за период: выберите договорную смету (эталон) и период, добавьте акты КС-2 — агент соберёт строки из принятых объёмов, сматчит их на позиции сметы, посчитает итоги по статьям и выдаст находки с HITL-разметкой. Неоднозначный матч разрешается выбором позиции вручную. Сборка идёт в очереди, статус обновляется сам.',
  },
  registers: {
    title: 'Накопительные таблицы (реестры)',
    subtitle:
      'Загрузите пакет первички за период (накладные ТОРГ-12/УПД и/или акты КС-2 в Excel) с тегами объект/участок. Агент построит накопительные реестры по месяцам и участкам и сводную аналитику (освоение по месяцам, по участкам, по поставщикам) — выгрузка в многолистовой Excel.',
  },
  accumulations: {
    title: 'Накопительный учёт ПА',
    subtitle:
      'Персистентная лента «данные ПА» по договору. Один раз загрузите существующую книгу «Накопительный учет ПА» (seed справочника расценок + истории), далее ежемесячно — пачку первичных актов: агент извлечёт выполненные за период объёмы, подставит цены из справочника, дозапишет строки в ленту и пересчитает сводные. Выгрузка — обновлённая книга (расценки / данные ПА / Лист1 / Лист2).',
  },
  'contract-lsr': {
    title: 'Локальные сметы договора',
    subtitle:
      'Эталон мира B: загрузите книгу смет Заказчика (выгрузка Гранд-Сметы, лист = смета) — агент разберёт позиции с расшифровкой цены: зарплата рабочих, эксплуатация машин, зарплата машинистов и трудозатраты. Именно из них заполняются графы 7-16 КС-2, М-29 и расчёт ФОТ. Повторная загрузка обновляет те же сметы по номеру.',
  },
  'material-norms': {
    title: 'Нормы расхода материалов',
    subtitle:
      'Коэффициенты потерь: сколько материала списывается сверх проектного объёма — резка трубы, бой кирпича, нахлёст рулона. Правило договора сильнее общего класса, поэтому нормы из приложений к конкретному договору заводите с указанием его id. Кнопка «какая норма подставится» показывает, какое правило сработает на конкретном наименовании из акта.',
  },
  'vat-rates': {
    title: 'Ставки НДС',
    subtitle:
      'Справочник ставок по периодам: ставка действует с указанной даты до следующей записи. Расчёты берут её по дате документа, поэтому прошлые ставки остаются в списке — они обслуживают документы своих лет. Новую ставку можно завести заранее. Тот же справочник виден экономисту.',
  },
  'norm-base': {
    title: 'Нормативная база ФСНБ-2022',
    subtitle:
      'Нормы ГЭСН с составом работ и ресурсов, поправочные коэффициенты ГГЭ, цены ФСБЦ и сборники ФСНБ-2020 — по действующей редакции, на которой считают все проверки расценок. Новое дополнение с ФГИС ЦС загружается архивом во вкладке «Редакции»: сначала на проверку с перечнем изменений, в действие — вручную.',
  },
  'fgiscs-periods': {
    title: 'Поквартальные индексы и ОЗП (ФГИС ЦС)',
    subtitle:
      'Просмотр загруженных через CLI периодов. Сравнение с активным кварталом и активация после ручного подтверждения — данные влияют на расчёты, молча переключать нельзя (ADR-0003).',
  },
  'rate-checks': {
    title: 'Применённые расценки (Гранд-Смета)',
    subtitle:
      'Загрузите Excel-выгрузку из Гранд-Сметы или смету в XML ГГЭ (.gge) — каждая локальная смета (ЛСР) и её позиции (шифры, цены ПЗ/ОЗП/ЭМ/ЗПМ/Мат, НР/СП %) попадут в базу и сверяются с нормативами: цены ФЕР, состав ГЭСН, накладные расходы и сметная прибыль. В смете из XML по нормам ГЭСН сверяются и поправочные коэффициенты (с привязкой ГГЭ), и материалы по проекту (с технологическими группами).',
  },
  'tender-estimates': {
    title: 'Сметный расчёт по тендеру',
    subtitle:
      'Загрузите перечень работ с объёмами (скелет Гранд-Сметы или ВОР, Excel/PDF). Агент построит локальную смету (ЛСР) по нормативу ФЕР/ГЭСН, найдёт типовые ошибки исходных данных и сравнит итог с аналогичными объектами (база прошлых расчётов + НЦС).',
  },
  'gs-checks': {
    title: 'Сверка Генподряд / Субподряд',
    subtitle:
      'Загрузите пакеты обеих сторон (КС-2/КС-3, договорная смета или ЛСР генподряда и субподрядчика). Агент сопоставит позиции по шифру и наименованию, посчитает маржу генподряда (по парам и тотально), покрытие (что отдано на субподряд vs выполняется своими силами) и расхождения объём/цена/сумма с HITL-разметкой и XLSX-отчётом.',
  },
  'zvi-checks': {
    title: 'Проверка ЗВИ (Запрос на Внесение Изменения)',
    subtitle:
      'Загрузите форму ЗВИ (xlsx) и, при наличии, ведомости РД (BOM/BOQ, PDF). Агент сверит объёмы по РД, единичные ставки ЕР с договорной сметой, арифметику (ПЗ = труд + оборуд + материалы; изменение ПЗ; косвенные) и пометит новые расценки, которых нет в договоре — расхождения с HITL-разметкой и XLSX-отчётом.',
  },
  chat: {
    title: 'Сметчик-ассистент',
    subtitle:
      'Диалог с ИИ-сметчиком по результатам всех функций: спросите про расхождения в проверке расценок, маржу в сверке ГП↔СП, баланс М-29, либо задайте методический вопрос (ФЕР/ГЭСН/НР-СП/БИМ/КС-2). Ассистент видит ваши прогоны и объясняет уже посчитанные числа — ничего не пересчитывая.',
  },
};

const QuickNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  margin-top: ${({ theme }) => theme.spacing(3)};
`;

const NavPill = styled(Link)<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(1)} ${({ theme }) => theme.spacing(2.5)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  text-decoration: none;
  border: 1px solid
    ${({ theme, $active }) => ($active ? theme.colors.primary : theme.colors.border)};
  background: ${({ theme, $active }) =>
    $active ? theme.colors.primarySoft : theme.colors.surface};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  transition: all ${({ theme }) => theme.motion.fast} ${({ theme }) => theme.motion.easeOut};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const QUICK_LINKS: Array<{ slug: string; label: string }> = [
  { slug: 'volume-checks', label: 'Ф1 · Объёмы' },
  { slug: 'rate-checks', label: 'Ф2 · Расценки' },
  { slug: 'ks-checks', label: 'Ф3 · Сверка КС' },
  { slug: 'ks-packages', label: 'Пакет акта КС-2/КС-3' },
  { slug: 'write-off-checks', label: 'Ф4 · М-29' },
  { slug: 'tender-estimates', label: 'Ф5 · Тендер ЛСР' },
  { slug: 'zvi-checks', label: 'Ф6 · ЗВИ' },
  { slug: 'registers', label: 'Ф7 · Реестры' },
  { slug: 'accumulations', label: 'Ф7B · Накопительная ПА' },
  { slug: 'gs-checks', label: 'Ф8 · ГП ↔ СП' },
  { slug: 'contract-lsr', label: 'Локальные сметы' },
  { slug: 'norm-base', label: 'Нормативная база' },
  { slug: 'chat', label: 'Ф9 · Ассистент' },
];

export function SmetaLayout() {
  const location = useLocation();
  const segments = location.pathname.split('/').filter(Boolean);
  // /agents/smeta/<section>
  const section = segments[2];
  const meta = section ? SECTIONS[section] : null;
  const isRoot = !section;
  const { can_read, can_write } = useAgentPermission('smeta');

  return (
    <Wrap>
      <Header>
        {isRoot ? (
          <>
            <Title>
              ОСНОВА<span className="accent">.Сметчик</span>
            </Title>
            <Subtitle>
              Сметно-ценовой отдел: проверка объёмов работ, единичных расценок, формы КС-2/КС-3,
              списание материалов и тендерные расчёты. Ниже — функции агента.
            </Subtitle>
          </>
        ) : (
          <>
            <TopRow>
              <Back to="/agents/smeta">
                <IconArrowLeft size={16} />
                <span>ОСНОВА.Сметчик</span>
              </Back>
            </TopRow>
            <SectionTitle>{meta?.title ?? section}</SectionTitle>
            {meta?.subtitle && <Subtitle>{meta.subtitle}</Subtitle>}
            <QuickNav aria-label="Быстрый переход по разделам Сметчика">
              {QUICK_LINKS.map((item) => (
                <NavPill
                  key={item.slug}
                  to={`/agents/smeta/${item.slug}`}
                  $active={section === item.slug}
                >
                  {item.label}
                </NavPill>
              ))}
            </QuickNav>
          </>
        )}
      </Header>
      {can_read && !can_write && <ReadOnlyBanner />}
      <Outlet />
    </Wrap>
  );
}
